<template>
  <div class="step-panel">
    <!-- Step Header -->
    <div class="step-header">
      <h2>{{ t('Deduções de Colaboradores') }}</h2>
    </div>

    <!-- Helper Banner -->
    <div class="helper-banner">
      <i class="pi pi-info-circle"></i>
      <div class="helper-content">
        <strong>{{ t('Deduções Independentes') }}</strong>
        <ul>
          <li>{{ t('Selecione deduções abertas ou parciais para descontar neste período') }}</li>
          <li>{{ t('Você pode descontar valores parciais de cada dedução') }}</li>
          <li>{{ t('O status será atualizado automaticamente (aberto → parcial → pago)') }}</li>
        </ul>
      </div>
    </div>

    <!-- Loading State from Machine -->
    <div v-if="isLoading" class="loading-container">
      <spinner />
    </div>

    <!-- Error State from Machine -->
    <div v-else-if="hasError" class="error-container">
      <inline-message severity="error" class="w-full">
        {{ t('Erro ao carregar deduções. Clique em tentar novamente.') }}
      </inline-message>
      <Button
        :label="t('Tentar Novamente')"
        icon="pi pi-refresh"
        data-testid="payroll-deductions-retry-btn"
        @click="handleRetry"
        class="mt-3"
      />
    </div>

    <!-- Interactive State -->
    <div v-else class="deductions-container">
      <!-- Employees with Deductions Summary Card -->
      <div class="deductions-summary-card">
        <PayrollConciliationStatusCard
          :status="conciliationStatus"
          :summary-items="summaryItems"
          :show-action="true"
          :action-label="t('Gerenciar Deduções')"
          :button-icon="deductionsSelected > 0 ? 'pi pi-pencil' : 'pi pi-plus-circle'"
          :button-severity="deductionsSelected > 0 ? 'warning' : 'success'"
          :status-message="statusMessage"
          @action="openDeductionsWizard"
        />
      </div>

      <!-- EmployeeDeductionsConciliationWizard -->
      <EmployeeDeductionsConciliationWizard
        v-if="periodId && franchiseId"
        :visible="showDeductionsWizard"
        :period-id="periodId"
        :franchise-id="franchiseId"
        :users-map="usersMap"
        :initial-selections="selectedDiscounts"
        @update:visible="showDeductionsWizard = $event"
        @update:selections="handleSelectionsUpdate"
        @completed="handleWizardCompleted"
        @closed="handleWizardClosed"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, inject, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '@/utils/DebugLogger';
import { eventBus } from '@/eventBus';
import PayrollConciliationStatusCard from './shared/PayrollConciliationStatusCard.vue';
import EmployeeDeductionsConciliationWizard from './EmployeeDeductionsConciliationWizard.vue';

type Id = number | string;

interface DeductionDiscount {
  deduction_id: Id;
  amount: number | string;
}

interface PaymentUser {
  user_id: Id;
  [key: string]: unknown;
}

interface MachineContext {
  period?: { id?: Id; franchise_id?: Id };
  periodFormData?: { franchise_id?: Id };
  error?: { message?: string };
  data?: {
    deductions?: {
      available?: unknown[];
      summary_by_employee?: unknown[];
      total?: number;
      selected?: Record<string, number>;
    };
  };
  deductionDiscounts?: DeductionDiscount[];
  paymentByUser?: PaymentUser[];
}

interface WizardMachine {
  context: { value: MachineContext };
  state: { value: { matches: (s: string) => boolean } };
  send: (event: { type: string; [key: string]: unknown }) => void;
}

// =============================================================================
// SINGLE SOURCE OF TRUTH: XState Machine
// =============================================================================

const machine = inject<WizardMachine>('wizardMachine')!;
const { t, n } = useI18n();

// =============================================================================
// MACHINE STATE READ-ONLY ACCESS
// =============================================================================

/**
 * Period ID from machine context
 */
const periodId = computed((): number | null => {
  const id = machine.context.value.period?.id;
  return id ? Number(id) : null;
});

/**
 * Franchise ID from machine context
 */
const franchiseId = computed((): number | null => {
  return Number(machine.context.value.period?.franchise_id) ||
         Number(machine.context.value.periodFormData?.franchise_id) ||
         null;
});

/**
 * Loading state from machine state
 */
const isLoading = computed((): boolean => {
  return machine.state.value.matches('employeeDeductions.loading');
});

/**
 * Error state from machine context
 */
const hasError = computed((): boolean => {
  return machine.state.value.matches('employeeDeductions.error');
});

/**
 * Selected discounts from machine context
 * Legacy field: deductionDiscounts
 * New structure: context.data.deductions.selected
 */
const selectedDiscounts = computed((): Record<string, number> => {
  const discounts = machine.context.value.deductionDiscounts ?? [];
  const selected: Record<string, number> = {};

  discounts.forEach((item: DeductionDiscount) => {
    const key = String(item.deduction_id);
    selected[key] = parseFloat(String(item.amount ?? 0)) || 0;
  });

  return selected;
});

/**
 * Build users map from payment by user data
 */
const usersMap = computed((): Record<string, PaymentUser> => {
  const users = machine.context.value.paymentByUser ?? [];
  const map: Record<string, PaymentUser> = {};
  users.forEach((u: PaymentUser) => {
    map[String(u.user_id)] = u;
  });
  return map;
});

// =============================================================================
// COMPUTED PROPERTIES (derived from machine state only)
// =============================================================================

/**
 * Count selected deductions
 */
const selectedCount = computed((): number => {
  return Object.keys(selectedDiscounts.value).length;
});

/**
 * Count deductions with amount > 0
 */
const deductionsSelected = computed((): number => {
  return Object.values(selectedDiscounts.value).filter(amount => (parseFloat(String(amount ?? 0)) || 0) > 0).length;
});

/**
 * Calculate total to discount
 */
const totalToDiscount = computed((): number => {
  return Object.values(selectedDiscounts.value).reduce(
    (sum: number, amount) => sum + (parseFloat(String(amount ?? 0)) || 0),
    0
  );
});

type ConciliationStatus = 'pending' | 'in_progress' | 'completed';

/**
 * Conciliation status based on selection
 */
const conciliationStatus = computed((): ConciliationStatus => {
  if (selectedCount.value === 0) return 'pending';
  return 'in_progress';
});

/**
 * Summary items for status card
 */
const summaryItems = computed(() => [
  {
    label: t('Deduções Selecionadas'),
    value: deductionsSelected.value.toString(),
    icon: 'pi pi-check-square',
  },
  {
    label: t('Valor a Descontar'),
    value: n(totalToDiscount.value, 'currency'),
    highlight: true,
    icon: 'pi pi-money-bill',
  },
]);

/**
 * Status message for card
 */
const statusMessage = computed((): string => {
  if (deductionsSelected.value === 0) {
    return t('Clique no botão para gerenciar deduções para desconto neste período.');
  }
  return t('{count} deduções selecionadas no valor de {total}.', {
    count: deductionsSelected.value,
    total: n(totalToDiscount.value, 'currency'),
  });
});

// =============================================================================
// UI STATE (component-scoped only)
// =============================================================================

/**
 * Wizard visibility - UI state only, not data
 */
const showDeductionsWizard = ref<boolean>(false);

// =============================================================================
// EVENT HANDLERS (send events to machine)
// =============================================================================

/**
 * Open deductions wizard
 */
const openDeductionsWizard = (): void => {
  log('PAYROLL_REFACTOR', 'REFACTOR_TRACE', 'StepEmployeeDeductions', 'OPEN_WIZARD', {
    selectedDiscountsKeys: Object.keys(selectedDiscounts.value),
    values: Object.values(selectedDiscounts.value),
    total: totalToDiscount.value,
  });
  showDeductionsWizard.value = true;
};

/**
 * Handle selections update from wizard
 * Send event to machine to update context
 */
const handleSelectionsUpdate = (newSelections: Record<string, number>): void => {
  const newTotal = Object.values(newSelections).reduce(
    (sum: number, amount: number) => sum + amount,
    0
  );

  log('PAYROLL_REFACTOR', 'DATA_FLOW', 'COMPONENT', 'WRITE', {
    via: 'machine.send()',
    event: 'UPDATE_DEDUCTION_SELECTIONS',
    selectionCount: Object.keys(newSelections).length,
    total: newTotal,
  });

  machine.send({
    type: 'UPDATE_DEDUCTION_SELECTIONS',
    selections: newSelections,
  });
};

/**
 * Handle wizard completed
 */
const handleWizardCompleted = (result: unknown): void => {
  log('PAYROLL_REFACTOR', 'REFACTOR_TRACE', 'StepEmployeeDeductions', 'WIZARD_COMPLETED', result);

  eventBus.emit('globalMessage.success', t('Deduções processadas com sucesso'));

  showDeductionsWizard.value = false;
  machine.send({ type: 'RELOAD_DEDUCTIONS' });
};

/**
 * Handle wizard closed
 */
const handleWizardClosed = (): void => {
  log('PAYROLL_REFACTOR', 'REFACTOR_TRACE', 'StepEmployeeDeductions', 'WIZARD_CLOSED');
  showDeductionsWizard.value = false;
};

/**
 * Handle retry on error
 */
const handleRetry = (): void => {
  log('PAYROLL_REFACTOR', 'REFACTOR_TRACE', 'StepEmployeeDeductions', 'RETRY_LOAD');
  machine.send({ type: 'RETRY_DEDUCTIONS' });
};

// =============================================================================
// LIFECYCLE & WATCH
// =============================================================================

/**
 * Watch for period changes to trigger data reload in machine
 * Note: Machine should automatically reload when period changes
 */
watch(periodId, (newId, oldId) => {
  if (newId && newId !== oldId) {
    log('PAYROLL_REFACTOR', 'REFACTOR_TRACE', 'StepEmployeeDeductions', 'PERIOD_CHANGED', {
      oldPeriodId: oldId,
      newPeriodId: newId,
      action: 'Machine will auto-reload deductions data',
    });
    // Machine handles reload via state transitions
  }
}, { immediate: false });
</script>

<style scoped>
.step-panel {
  height: 100%;
}

.step-header {
  margin-bottom: 1.5rem;
}

.step-header h2 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-color);
}

.helper-banner {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  background: var(--blue-50);
  border-left: 3px solid var(--blue-500);
  border-radius: 6px;
  margin-bottom: 1.5rem;
}

.helper-banner i {
  color: var(--blue-500);
  font-size: 1.25rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.helper-content strong {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--text-color);
}

.helper-content ul {
  margin: 0;
  padding-left: 1.25rem;
}

.helper-content li {
  margin-bottom: 0.25rem;
  color: var(--text-color-secondary);
  font-size: 0.9rem;
}

.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 3rem;
}

.deductions-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.employees-list-card {
  background: var(--surface-card);
  border-radius: 8px;
  border: 1px solid var(--surface-border);
  overflow: hidden;
}

.employees-list-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  background: var(--surface-50);
  border-bottom: 1px solid var(--surface-border);
}

.employees-list-header i {
  color: var(--primary-color);
  font-size: 1.25rem;
}

.employees-list-header h4 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-color);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  text-align: center;
}

.empty-state i {
  font-size: 3rem;
  color: var(--green-500);
  margin-bottom: 1rem;
}

.empty-state h4 {
  margin: 0 0 0.5rem 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-color);
}

.empty-state p {
  margin: 0;
  color: var(--text-color-secondary);
}

.employees-list {
  padding: 1.5rem;
}

.deductions-summary-card {
  margin-bottom: 1.5rem;
}

.w-full {
  width: 100%;
}

.mt-3 {
  margin-top: 0.75rem;
}
</style>
